import { type CountryComparisonResult, payPeriodLabels, payPeriodMultipliers, type PayPeriod } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface ComparisonTableProps {
  results: CountryComparisonResult[];
  displayPeriod: PayPeriod;
  baseCountryCode?: string;
}

export function ComparisonTable({ results, displayPeriod, baseCountryCode }: ComparisonTableProps) {
  if (results.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Enter a salary to see country comparisons
        </CardContent>
      </Card>
    );
  }

  const multiplier = payPeriodMultipliers[displayPeriod];
  const baseResult = baseCountryCode 
    ? results.find(r => r.country.code === baseCountryCode)
    : results[0];

  const formatCurrency = (amount: number, symbol: string) => {
    const displayAmount = amount / multiplier;
    return `${symbol}${displayAmount.toLocaleString("en-US", { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span>Your Salary in All Countries</span>
          <span className="text-sm font-normal text-muted-foreground">
            Per {payPeriodLabels[displayPeriod]}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full min-w-0">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-1 sm:px-2 text-xs sm:text-sm font-medium text-muted-foreground">Country</th>
                <th className="text-right py-3 px-1 sm:px-2 text-xs sm:text-sm font-medium text-muted-foreground">Tax</th>
                <th className="text-right py-3 px-1 sm:px-2 text-xs sm:text-sm font-medium text-muted-foreground">Take-Home</th>
                <th className="text-right py-3 px-1 sm:px-2 text-xs sm:text-sm font-medium text-muted-foreground hidden sm:table-cell">Diff</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => {
                const difference = baseResult 
                  ? (result.netSalary - baseResult.netSalary) / multiplier
                  : 0;
                const isBase = result.country.code === baseCountryCode;

                return (
                  <tr 
                    key={result.country.code} 
                    className={`
                      border-b border-border last:border-0 transition-colors
                      ${isBase ? "bg-primary/5" : "hover:bg-muted/50"}
                    `}
                    data-testid={`row-comparison-${result.country.code}`}
                  >
                    <td className="py-3 px-1 sm:px-2">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="text-lg sm:text-xl">{result.country.flag}</span>
                        <span className="font-medium text-xs sm:text-sm truncate max-w-[80px] sm:max-w-none">{result.country.name}</span>
                        {index === 0 && (
                          <span className="text-[10px] sm:text-xs bg-success/10 text-success px-1 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                            Best
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-1 sm:px-2 text-right">
                      <span className="font-mono text-xs sm:text-sm">
                        {result.effectiveTaxRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-1 sm:px-2 text-right">
                      <span className="font-mono font-semibold text-xs sm:text-sm">
                        {formatCurrency(result.netSalary, result.country.symbol)}
                      </span>
                    </td>
                    <td className="py-3 px-1 sm:px-2 text-right hidden sm:table-cell">
                      {isBase ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <DifferenceIndicator 
                          difference={difference} 
                          symbol={result.country.symbol}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

interface DifferenceIndicatorProps {
  difference: number;
  symbol: string;
}

function DifferenceIndicator({ difference, symbol }: DifferenceIndicatorProps) {
  const absValue = Math.abs(difference);
  const formatted = `${symbol}${absValue.toLocaleString("en-US", { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  })}`;

  if (difference > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-success font-mono text-sm">
        <TrendingUp className="h-3 w-3" />
        +{formatted}
      </span>
    );
  } else if (difference < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-destructive font-mono text-sm">
        <TrendingDown className="h-3 w-3" />
        -{formatted}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <Minus className="h-3 w-3" />
      {formatted}
    </span>
  );
}
